import React from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { selectNotificationGroupList } from '../../../../redux/slices/notificationGroupsSlice/notificationGroupsSlice';
import { selectQuietTimes } from '../../../../redux/slices/quietTimesSlice/quietTimesSlice';
import { transportTypeDescriptionMap } from '../../../../utility/NotificationGroupUtility';
import TransportType from '../../../../utility/types/TransportType';
import {
  selectNotificationTemplates,
} from '../../../../redux/slices/notificationTemplatesSlice/notificationTemplatesSlice';
import { delayIntervalList } from '../../../../utility/AlertEditorUtils';

export const formatRecipientsText = (recipients = [], users = []) => recipients
  .map((recipient) => {
    const transportLabel = transportTypeDescriptionMap[recipient.transportId] || '';
    if (recipient.userId && users.length) {
      const user = users.find(
        (u) => String(u.userId) === String(recipient.userId),
      );
      if (!user) return null;
      const { firstName, lastName } = user;
      return `${transportLabel} ${firstName} ${lastName}`.trim();
    }
    if (!recipient.context) return null;
    let context;
    try {
      context = JSON.parse(recipient.context);
    } catch {
      return null;
    }
    switch (recipient.transportId) {
      case TransportType.EMAIL:
        return context.emailAddress
          ? `${transportLabel} ${context.emailAddress}`.trim()
          : null;
      case TransportType.WEBHOOK:
        return context.url
          ? `${transportLabel} ${context.url}`.trim()
          : null;
      default:
        return null;
    }
  })
  .filter(Boolean)
  .join(', ');

export const combineNotificationGroupRecipients = (notificationGroups) => notificationGroups
  .flatMap((group) => (Array.isArray(group.recipients) ? group.recipients : []));

const ActionSummaryRow = ({ action, usersList }) => {
  const notificationGroups = useSelector(selectNotificationGroupList);
  const quietTimes = useSelector(selectQuietTimes);
  const customTemplates = useSelector(selectNotificationTemplates);
  const [showRecipients, setShowRecipients] = React.useState(false);

  const notificationActionGroups = notificationGroups
    .filter((group) => action.notificationGroupIdList.includes(group.id));

  const notificationGroupNames = notificationActionGroups
    .map((ng) => ng.name)
    .join(', ');

  const recipients = combineNotificationGroupRecipients(notificationActionGroups);

  const formattedRecipientText = formatRecipientsText(recipients, usersList) || 'no recipients';

  const getActionDetailsDescription = () => {
    const lines = [];

    if (action.repeatEnabled) {
      const maxRepeats = action.maxReminders
        ? `, max ${action.maxReminders} times`
        : '';

      lines.push(
        <div key="repeat">
          Repeat every
          {' '}
          {action.repeatInterval}
          {' '}
          minutes
          {maxRepeats}
        </div>,
      );
    }

    if (action.delay != null && action.delay > 0) {
      const delayInterval = delayIntervalList.find((interval) => interval.value === action.delay);
      lines.push(
        <div key="delay">
          Delay action by
          {' '}
          {delayInterval ? delayInterval.name : `${action.delay} minutes`}
        </div>,
      );
    }

    if (action.quietTimeId != null) {
      const quietTime = quietTimes.find((qt) => qt.id === action.quietTimeId);
      if (quietTime) {
        lines.push(
          <div key="schedule">
            Use schedule
            {' '}
            {quietTime.name}
          </div>,
        );
      }
    }

    if (action.templateId != null) {
      const template = customTemplates.find((tmpl) => tmpl.id === action.templateId);
      if (template) {
        lines.push(
          <div key="schedule">
            Use message template
            {' '}
            {template.name}
          </div>,
        );
      }
    }

    if (lines.length === 0) {
      return null;
    }

    return <>{lines}</>;
  };

  const advancedAlertingOptions = getActionDetailsDescription();
  return (
    <>
      <Row>
        <Col>
          Send Notification to:
          {' '}
          <strong>
            {notificationGroupNames}
          </strong>
          <div className="mt-2">
            <p>
              { showRecipients && (
                formattedRecipientText
              )}
            </p>

            <button
              type="button"
              className="btn btn-link text-muted"
              onClick={() => setShowRecipients(!showRecipients)}
            >
              {showRecipients ? 'Hide' : 'Show'}
              {' '}
              recipients
            </button>
          </div>
        </Col>
        {
          advancedAlertingOptions && (
          <Col className="border-left">
            {getActionDetailsDescription()}
          </Col>
          )
        }
      </Row>
    </>
  );
};

ActionSummaryRow.propTypes = {
  action: PropTypes.shape({
    notificationGroupIdList: PropTypes.arrayOf(PropTypes.number).isRequired,

    repeatEnabled: PropTypes.bool.isRequired,
    repeatInterval: PropTypes.number, // minutes
    maxReminders: PropTypes.number, // nullable
    templateId: PropTypes.number,
    delay: PropTypes.number, // nullable, minutes

    quietTimeId: PropTypes.number, // nullable
  }).isRequired,
  usersList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default ActionSummaryRow;
